import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, of, throwError } from "rxjs";

const STAFF_MICROS = [
  "/ms-hr/",
  "/ms-agreement/",
  "/ms-master-data/",
  "/ms-organization/",
  "/ms-people/",
];

const STAFF_REAL_DATA_ROUTES = new Set([
  "/admin/staff/payslips",
  "/admin/staff/employees/management",
  "/admin/staff/employees/management/new",
  "/admin/staff/employees/cesados",
  "/admin/staff/employees/contracts",
]);

const NOW = "2026-08-13T09:15:00-05:00";

const COMPANIES = [
  company(
    1,
    "KeOla Networks S.A.",
    "KeOla Networks",
    "20607408387",
    "Lima",
    "San Isidro",
  ),
  company(2, "INCLUB S.A.C.", "Inclub", "20548796321", "Lima", "San Borja"),
  company(
    3,
    "RIBERA DEL RÍO S.A.C.",
    "Ribera del Río",
    "20458963217",
    "Lima",
    "Miraflores",
  ),
];

const EMPLOYEES = [
  employee(
    1,
    1,
    "EMP-001",
    "Kevin Alexander",
    "Sánchez",
    "Rojas",
    "71288112",
    "MALE",
    1,
    "Backend",
  ),
  employee(
    2,
    1,
    "EMP-002",
    "Claudia Milagros",
    "Maldonado",
    "Díaz",
    "45879632",
    "FEMALE",
    2,
    "Analista",
  ),
  employee(
    3,
    2,
    "EMP-003",
    "Amirander Joel",
    "Arbieto",
    "Campos",
    "73458921",
    "MALE",
    3,
    "Jefe TI",
  ),
  employee(
    4,
    2,
    "EMP-004",
    "Jesús Alberto",
    "Silva",
    "Morales",
    "61234578",
    "MALE",
    2,
    "Analista",
  ),
  employee(
    5,
    3,
    "EMP-005",
    "Angélica Rosario",
    "Casas",
    "Rengifo",
    "48963215",
    "FEMALE",
    4,
    "Diseñadora UX/UI",
  ),
  employee(
    6,
    3,
    "EMP-006",
    "Fredder Fernando",
    "Rojas",
    "Contreras",
    "81221734",
    "MALE",
    5,
    "Frontend",
  ),
];

const CONTRACTS = EMPLOYEES.map((item, index) => ({
  contractId: index + 1,
  employeeId: item.employeeId,
  contractTypeId: 1,
  continuityTypeId: 1,
  renewalLimitId: 2,
  workingModeId: (index % 3) + 1,
  startDate: "2025-04-21",
  endDate: index === 1 ? "2026-11-02" : "2026-10-21",
  currencyCode: "PEN",
  baseSalary: index === 4 ? 2000 : 3500,
  performanceBonus: 150,
  computerAllowance: 100,
  internetAllowance: 100,
  electricityAllowance: 90,
  transportationAllowance: 0,
  contractUrl: "/assets/demo/contrato-modelo.pdf",
  status: index === 1 || index === 5 ? "ACTIVE" : "DRAFT",
  activatedAt: index === 1 || index === 5 ? NOW : null,
  terminatedAt: null,
  terminationReason: null,
  createdAt: NOW,
  updatedAt: NOW,
}));

const CONTRACT_TYPES = [
  catalogItem(1, "PAYROLL", "Planilla"),
  catalogItem(2, "SERVICE", "Locación de servicios"),
];
const CONTINUITY_TYPES = [
  catalogItem(1, "CONTINUES", "Sí, actualmente tiene este cargo"),
  catalogItem(2, "TRIAL", "Solo durante el periodo de prueba"),
];
const RENEWAL_LIMITS = [
  catalogItem(1, "FIRST", "Primera contratación"),
  catalogItem(2, "ONE", "1 vez"),
  catalogItem(3, "TWO", "2 veces"),
];
const WORKING_MODES = [
  catalogItem(1, "REMOTE", "Remoto"),
  catalogItem(2, "HYBRID", "Híbrido"),
  catalogItem(3, "ONSITE", "Presencial"),
];

const BENEFIT_CATEGORIES = [
  {
    categoryId: 1,
    code: "RECREATIONAL",
    name: "Beneficios recreativos",
    description: "Experiencias y recreación",
    sortOrder: 1,
    active: true,
  },
  {
    categoryId: 2,
    code: "TIME_OFF",
    name: "Beneficios de tiempo libre",
    description: "Días y vales de tiempo",
    sortOrder: 2,
    active: true,
  },
  {
    categoryId: 3,
    code: "HEALTH",
    name: "Salud y bienestar",
    description: "Prevención y bienestar",
    sortOrder: 3,
    active: true,
  },
  {
    categoryId: 4,
    code: "PERSONAL",
    name: "Licencias personales",
    description: "Licencias especiales",
    sortOrder: 4,
    active: true,
  },
];

const BENEFITS = [
  benefit(
    1,
    "FULL_DAY",
    "Full day en Club Ribera del Río",
    "RECREATIONAL",
    "https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&w=900&q=80",
  ),
  benefit(
    2,
    "BIRTHDAY",
    "Medio día por cumpleaños",
    "TIME_OFF",
    "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=900&q=80",
  ),
  benefit(
    3,
    "HEALTH_DAY",
    "Día libre por prevención",
    "HEALTH",
    "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=900&q=80",
  ),
  benefit(
    4,
    "MOVING",
    "Un día por mudanza",
    "PERSONAL",
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=900&q=80",
  ),
];

/**
 * API-first fallback for the new Staff workspace.
 *
 * It never short-circuits a healthy API and never runs in the historical
 * Recursos Humanos routes. This lets the shared application keep its current
 * behaviour while the new Staff micros are being deployed independently.
 */
export const staffMockFallbackInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!shouldFallback(router.url, req, error)) {
        return throwError(() => error);
      }

      return of(mockResponse(req));
    }),
  );
};

function shouldFallback(
  routeUrl: string,
  request: HttpRequest<unknown>,
  error: unknown,
): boolean {
  if (!routeUrl.startsWith("/admin/staff")) return false;
  const routePath = routeUrl.split(/[?#]/, 1)[0].replace(/\/$/, "");
  if (STAFF_REAL_DATA_ROUTES.has(routePath)) return false;
  if (/\/ms-hr\/api\/v1\/(?:hr\/)?vacation(?:[-\/]|$)/.test(request.url)) return false;
  if (!STAFF_MICROS.some((path) => request.url.includes(path))) return false;
  if (!(error instanceof HttpErrorResponse)) return false;
  return error.status === 0 || error.status === 404 || error.status >= 500;
}

function mockResponse(request: HttpRequest<unknown>): HttpResponse<unknown> {
  const path = request.url.split("?")[0].replace(/^https?:\/\/[^/]+/, "");
  const body =
    request.method === "GET" ? mockGet(path) : mockMutation(path, request.body);

  return new HttpResponse({
    status: 200,
    body,
    url: request.url,
    headers: request.headers.set("X-Klab-Data-Source", "staff-mock-fallback"),
  });
}

function mockGet(path: string): unknown {
  if (path.includes("/ms-notification/")) return notificationGet(path);
  if (path.includes("/ms-people/")) return peopleGet(path);

  if (path.includes("/employee-documents")) return [];
  if (path.includes("/employees"))
    return oneOrMany(path, "employees", EMPLOYEES, "employeeId");

  if (path.endsWith("/contract-types")) return CONTRACT_TYPES;
  if (path.endsWith("/continuity-types")) return CONTINUITY_TYPES;
  if (path.endsWith("/renewal-limits")) return RENEWAL_LIMITS;
  if (path.endsWith("/working-modes")) return WORKING_MODES;
  if (/\/contracts\/\d+\/history$/.test(path)) return [];
  if (path.includes("/contracts"))
    return oneOrMany(path, "contracts", CONTRACTS, "contractId");

  if (path.endsWith("/benefit-categories")) return BENEFIT_CATEGORIES;
  if (path.includes("/benefits"))
    return oneOrMany(path, "benefits", BENEFITS, "benefitId");
  if (/\/benefit-requests\/\d+$/.test(path)) return benefitRequests()[0];
  if (path.endsWith("/benefit-requests")) return benefitRequests();

  if (path.endsWith("/agreement-templates")) return agreementTemplates();
  if (/\/agreements\/\d+\/signatures$/.test(path)) return [];
  if (path.includes("/agreements"))
    return oneOrMany(path, "agreements", agreements(), "agreementId");

  if (path.endsWith("/locations/departments")) return geoDepartments();
  if (path.includes("/locations/departments/") && path.endsWith("/provinces"))
    return geoProvinces();
  if (path.includes("/locations/provinces/") && path.endsWith("/districts"))
    return geoDistricts();
  if (path.includes("/document_types")) return documentTypes();
  if (path.includes("/countries")) return countries();
    if (path.includes("/currency")) return currencies();
  if (path.includes("/tax-units")) return taxUnits();
  if (path.includes("/pension-funds")) return pensionFunds();
  if (path.includes("/financial_institution")) return bankEntities();
  if (path.includes("/worker-types")) return workerTypes();
  if (path.includes("/sctr")) return sctrProviders();
  if (path.includes("/taxes")) return taxRates();
  if (/\/catalogs\/\d+\/items/.test(path)) return catalogItems();
  if (path.includes("/catalogs")) return catalogs();
  if (path.includes("/customers")) return customers();
  if (path.includes("/products")) return products();

  if (path.includes("/legal-representatives"))
    return oneOrMany(
      path,
      "legal-representatives",
      legalRepresentatives(),
      "legalRepresentativeId",
    );
  if (path.includes("/payroll-responsibles"))
    return oneOrMany(
      path,
      "payroll-responsibles",
      payrollResponsibles(),
      "payrollResponsibleId",
    );
  if (path.includes("/bank-accounts"))
    return oneOrMany(path, "bank-accounts", bankAccounts(), "bankAccountId");
  if (path.includes("/company-parameters"))
    return companyResource(path, companyParameters());
  if (path.includes("/company-law-modifications"))
    return companyResource(path, lawModifications());
  if (path.includes("/company-digital-payslips"))
    return companyResource(path, digitalPayslips());
  if (path.endsWith("/locations")) return [pagedLocations()];
  if (path.endsWith("/departments")) return organizationDepartments();
  if (path.includes("/branches")) return branches();
  if (path.includes("/cost-centers")) return costCenters();
  if (path.includes("/positions"))
    return oneOrMany(path, "positions", positions(), "positionId");
  if (path.includes("/companies"))
    return oneOrMany(path, "companies", COMPANIES, "companyId");

  return [];
}

function mockMutation(path: string, body: unknown): unknown {
  const payload =
    body && typeof body === "object" ? (body as Record<string, unknown>) : {};
  if (path.includes("/ms-notification/"))
    return notificationMutation(path, payload);
  if (path.includes("/ms-people/")) return peopleMutation(path, payload);

  if (path.includes("/employee-documents"))
    return employeeDocumentMutation(path, payload);
  if (path.includes("/employees"))
    return {
      ...EMPLOYEES[0],
      ...payload,
      employeeId: numericTail(path) || 99,
      updatedAt: NOW,
    };
  if (path.includes("/contracts"))
    return {
      ...CONTRACTS[0],
      ...payload,
      contractId: numericTail(path) || 99,
      updatedAt: NOW,
    };
  if (path.includes("/benefit-requests"))
    return {
      ...benefitRequests()[0],
      ...payload,
      benefitRequestId: numericTail(path) || 99,
    };
  if (path.includes("/benefits"))
    return {
      ...BENEFITS[0],
      ...payload,
      benefitId: numericTail(path) || 99,
      updatedAt: NOW,
    };
  if (path.includes("/agreements")) return agreementMutation(path, payload);

  const masterData = masterDataMutation(path, payload);
  if (masterData !== undefined) return masterData;

  const resources: Array<[string, Record<string, unknown>, string]> = [
    [
      "legal-representatives",
      legalRepresentatives()[0],
      "legalRepresentativeId",
    ],
    ["payroll-responsibles", payrollResponsibles()[0], "payrollResponsibleId"],
    ["bank-accounts", bankAccounts()[0], "bankAccountId"],
    ["company-parameters", companyParameters()[0], "companyParameterId"],
    [
      "company-law-modifications",
      lawModifications()[0],
      "companyLawModificationId",
    ],
    [
      "company-digital-payslips",
      digitalPayslips()[0],
      "companyDigitalPayslipId",
    ],
    ["positions", positions()[0], "positionId"],
    ["companies", COMPANIES[0], "companyId"],
  ];
  const resource = resources.find(([segment]) => path.includes(`/${segment}`));
  if (resource)
    return {
      ...resource[1],
      ...payload,
      [resource[2]]: numericTail(path) || 99,
    };

  return {
    ...payload,
    id: numericTail(path) || 99,
    active: true,
    createdAt: NOW,
    updatedAt: NOW,
  };
}

function employeeDocumentMutation(
  path: string,
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const match = path.match(/\/employee-documents\/(\d+)/);
  return {
    employeeDocumentId: Number(match?.[1]) || 99,
    employeeId: Number(payload["employeeId"]) || 1,
    documentTypeCode: payload["documentTypeCode"] ?? "CONTRACT",
    name: payload["name"] ?? "Documento demo",
    documentUrl: payload["documentUrl"] ?? "/assets/demo/contrato-modelo.pdf",
    issuedAt: payload["issuedAt"] ?? null,
    expiresAt: payload["expiresAt"] ?? null,
    status: path.endsWith("/archive") ? "ARCHIVED" : "ACTIVE",
    registeredByUserId: 1,
    createdAt: NOW,
    updatedAt: NOW,
  };
}

function agreementMutation(
  path: string,
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const agreementId = Number(path.match(/\/agreements\/(\d+)/)?.[1]) || 99;
  const partyId = Number(path.match(/\/parties\/(\d+)/)?.[1]) || 1;

  if (/\/parties\/\d+\/sign$/.test(path)) {
    return {
      signatureId: 99,
      agreementId,
      versionId: 1,
      partyId,
      status: "SIGNED",
      provider: payload["provider"] ?? "DEMO",
      externalSignatureId: payload["externalSignatureId"] ?? "DEMO-99",
      evidenceUrl: payload["evidenceUrl"] ?? "/assets/demo/firma.png",
      signedByUserId: 1,
      requestedAt: NOW,
      signedAt: NOW,
      updatedAt: NOW,
    };
  }

  const current = { ...agreements()[0], ...payload, agreementId };
  if (path.endsWith("/generate")) {
    return {
      ...current,
      status: "GENERATED",
      currentDocumentUrl: "/assets/demo/contrato-modelo.pdf",
      generatedAt: NOW,
      updatedAt: NOW,
    };
  }
  if (path.endsWith("/submit-signature")) {
    return {
      ...current,
      status: "PENDING_SIGNATURE",
      submittedAt: NOW,
      updatedAt: NOW,
    };
  }
  if (path.endsWith("/terminate")) {
    return {
      ...current,
      status: "TERMINATED",
      terminatedAt: NOW,
      terminationReason: payload["reason"] ?? "Cierre de contrato",
      updatedAt: NOW,
    };
  }
  if (path.endsWith("/cancel")) {
    return { ...current, status: "CANCELLED", updatedAt: NOW };
  }
  return { ...current, updatedAt: NOW };
}

function masterDataMutation(
  path: string,
  payload: Record<string, unknown>,
): Record<string, unknown> | null | undefined {
  if (path.includes("/document_types")) {
    const code = String(payload["rtpsCode"] ?? path.split("/").at(-1) ?? "99");
    return {
      ...documentTypes()[0],
      name: payload["name"] ?? documentTypes()[0].name,
      code: code === "document-types" ? "RTPS_99" : `RTPS_${code}`,
      authorityCode: code,
      maxLength: Number(payload["validationLength"]) || 8,
    };
  }
  if (path.includes("/currency")) {
    const code = String(
      payload["abbreviation"] ?? path.split("/").at(-1) ?? "PEN",
    );
    const id = Number(path.split("/").at(-1)) || 99;
    return { ...currencies()[0], idCurrency: id, ...payload, code: code.toUpperCase() };
  }
  if (path.includes("/tax-units"))
    return {
      ...taxUnits()[0],
      ...payload,
      taxUnitId: numericTail(path) || 99,
      updatedAt: NOW,
    };
  if (path.includes("/pension-funds"))
    return {
      ...pensionFunds()[0],
      ...payload,
      pensionFundId: numericTail(path) || 99,
      abbreviation: String(payload["name"] ?? "AFP")
        .slice(0, 3)
        .toUpperCase(),
      updatedAt: NOW,
    };
  if (path.includes("/sctr"))
    return {
      ...sctrProviders()[0],
      ...payload,
      sctrId: numericTail(path) || 99,
      updatedAt: NOW,
    };
  if (path.includes("/financial_institution"))
    return {
      ...bankEntities()[0],
      ...payload,
      bankEntityId: numericTail(path) || 99,
      updatedAt: NOW,
    };
  if (path.includes("/worker-types"))
    return {
      ...workerTypes()[0],
      ...payload,
      workerTypeId: numericTail(path) || 99,
      updatedAt: NOW,
    };
  if (path.includes("/taxes"))
    return {
      ...taxRates()[0],
      ...payload,
      taxRateId: numericTail(path) || 99,
      updatedAt: NOW,
    };
  if (/\/catalogs\/\d+\/items/.test(path))
    return {
      ...catalogItems()[0],
      ...payload,
      itemId: numericTail(path) || 99,
      updatedAt: NOW,
    };
  if (path.includes("/catalogs"))
    return {
      ...catalogs()[0],
      ...payload,
      catalogId: numericTail(path) || 99,
      updatedAt: NOW,
    };
  if (path.includes("/customers"))
    return {
      ...customers()[0],
      ...payload,
      customerId: numericTail(path) || 99,
      updatedAt: NOW,
    };
  if (path.includes("/products"))
    return {
      ...products()[0],
      ...payload,
      productId: numericTail(path) || 99,
      updatedAt: NOW,
    };
  return undefined;
}

function oneOrMany<T extends Record<string, unknown>>(
  path: string,
  resource: string,
  items: T[],
  idKey: keyof T,
): T | T[] {
  const match = path.match(new RegExp(`/${resource}/(\\d+)(?:/|$)`));
  return match ? findBy(items, idKey, Number(match[1])) : items;
}

function companyResource<T extends Record<string, unknown>>(
  path: string,
  items: T[],
): T | T[] {
  const match = path.match(/\/company\/(\d+)(?:\/|$)/);
  return match
    ? findBy(items, "companyId" as keyof T, Number(match[1]))
    : items;
}

function findBy<T extends Record<string, unknown>>(
  items: T[],
  key: keyof T,
  id: number,
): T {
  return items.find((item) => Number(item[key]) === id) ?? items[0];
}

function numericTail(path: string): number {
  const values = path.match(/\d+/g);
  return Number(values?.at(-1) ?? 0);
}

function notificationGet(path: string): unknown {
  if (path.includes("/notification-templates")) return notificationTemplates();
  if (/\/notifications\/\d+$/.test(path))
    return findBy(notifications(), "notificationId", numericTail(path));
  if (path.endsWith("/notifications")) return notifications();
  return [];
}

function notificationMutation(
  path: string,
  payload: Record<string, unknown>,
): unknown {
  if (path.endsWith("/read-all")) return null;
  if (/\/notifications\/\d+\/read$/.test(path))
    return {
      ...findBy(notifications(), "notificationId", numericTail(path)),
      readAt: NOW,
      updatedAt: NOW,
    };
  if (path.includes("/notification-templates"))
    return {
      ...notificationTemplates()[0],
      ...payload,
      templateId: numericTail(path) || 99,
      updatedAt: NOW,
    };
  if (path.endsWith("/notifications"))
    return {
      ...notifications()[0],
      ...payload,
      notificationId: 99,
      recipientUserId: Number(payload["recipientUserId"]) || 1001,
      createdAt: NOW,
      updatedAt: NOW,
    };
  return null;
}

function peopleGet(path: string): unknown {
  if (/\/requests\/\d+$/.test(path))
    return findBy(peopleRequests(), "requestId", numericTail(path));
  if (path.endsWith("/requests") || path.endsWith("/requests/me"))
    return paged(peopleRequests());
  if (/\/employees\/\d+$/.test(path))
    return findBy(peopleEmployees(), "employeeId", numericTail(path));
  if (path.endsWith("/employees")) return paged(peopleEmployees());
  return [];
}

function peopleMutation(
  path: string,
  payload: Record<string, unknown>,
): unknown {
  if (path.includes("/requests"))
    return {
      ...peopleRequests()[0],
      ...payload,
      requestId: numericTail(path) || 99,
      updatedAt: NOW,
    };
  return null;
}

function paged<T>(content: T[]) {
  return {
    content,
    page: {
      number: 0,
      size: Math.max(content.length, 1),
      totalElements: content.length,
      totalPages: 1,
    },
  };
}

function notifications() {
  return [
    notification(
      1,
      "PRODUCTIVITY_ALERT",
      "Primer aviso",
      "Tu productividad del día no alcanzó el porcentaje requerido.",
      null,
    ),
    notification(
      2,
      "PAYSLIP_SIGNATURE",
      "Falta de firma en boleta",
      "Tienes una boleta de pago pendiente de firma.",
      null,
    ),
    notification(
      3,
      "ANNIVERSARY",
      "¡Felicitaciones por el año cumplido!",
      "Gracias por formar parte de nuestro equipo.",
      "2026-08-08T10:00:00-05:00",
    ),
  ];
}

function notification(
  notificationId: number,
  notificationType: string,
  subject: string,
  body: string,
  readAt: string | null,
) {
  return {
    notificationId,
    recipientUserId: 1001,
    recipientAddress: null,
    notificationType,
    channel: "IN_APP",
    subject,
    body,
    data: {
      dispatchId: `DEMO-${notificationId}`,
      templateId: String(notificationId),
      templateName: subject,
      senderName: "Recursos Humanos",
      employeeId: 1,
      employeeName: "Kevin Alexander Sánchez Rojas",
      email: "kevin.sanchez@klab.pe",
      route: "/admin/notifications/history",
    },
    status: "SENT",
    readAt,
    createdByUserId: 1001,
    createdAt: `2026-08-${String(13 - notificationId).padStart(2, "0")}T09:15:00-05:00`,
    updatedAt: NOW,
  };
}

function notificationTemplates() {
  return [
    {
      templateId: 1,
      code: "MANUAL_FALTA_FIRMA",
      channel: "IN_APP",
      subjectTemplate: "Falta de firma",
      bodyTemplate: "Recuerda revisar y firmar tu documento pendiente.",
      active: true,
      createdAt: NOW,
      updatedAt: NOW,
    },
    {
      templateId: 2,
      code: "MANUAL_ANIVERSARIO",
      channel: "IN_APP",
      subjectTemplate: "Año de trabajo",
      bodyTemplate: "¡Felicitaciones por cumplir un año en la organización!",
      active: true,
      createdAt: NOW,
      updatedAt: NOW,
    },
  ];
}

function peopleEmployees() {
  return EMPLOYEES.map((item, index) => ({
    employeeId: item.employeeId,
    userId: item.identityUserId,
    code: item.employeeCode,
    name: item.firstName,
    lastname: `${item.lastName} ${item.secondLastName}`,
    email: item.corporateEmail,
    timezone: "America/Lima",
    location: { locationId: (index % 3) + 1, name: "Lima" },
    department: {
      departmentId: item.departmentId,
      name: [
        "Recursos Humanos",
        "Tecnología",
        "Marketing",
        "Finanzas",
        "Operaciones",
      ][index % 5],
    },
    role: {
      roleId: item.positionId,
      name: ["Backend", "Analista", "Jefe TI", "Diseñadora UX/UI", "Frontend"][
        index % 5
      ],
    },
    schedule: { scheduleId: 1, name: "Horario regular" },
    leaderEmployeeId: null,
    isLeader: index === 2,
  }));
}

function peopleRequests() {
  return EMPLOYEES.slice(0, 4).map((item, index) => ({
    requestId: index + 1,
    employee: {
      employeeId: item.employeeId,
      code: item.employeeCode,
      name: item.firstName,
      lastname: `${item.lastName} ${item.secondLastName}`,
    },
    approver: {
      employeeId: 3,
      code: "EMP-003",
      name: "Amirander Joel",
      lastname: "Arbieto Campos",
    },
    requestDate: `2026-08-${String(10 + index).padStart(2, "0")}`,
    startTime: "18:00:00",
    endTime: "20:00:00",
    totalTime: "02:00:00",
    type: index % 2 ? "MEDICAL_APPOINTMENT" : "OTHER",
    reason: index % 2 ? "HOURS_JUSTIFICATION" : "OVERTIME",
    detailJustification: "Solicitud demostrativa para validar la vista Staff.",
    status: index === 1 ? "APPROVED" : index === 2 ? "REJECTED" : "PENDING",
    attachments: [],
    createdAt: NOW,
    updatedAt: NOW,
  }));
}

function pagedLocations() {
  return paged([
    {
      locationId: 1,
      name: "Lima - San Isidro",
      status: "ACTIVE",
      departments: [
        { departmentId: 1, name: "Recursos Humanos" },
        { departmentId: 2, name: "Tecnología" },
      ],
    },
    {
      locationId: 2,
      name: "Lima - Miraflores",
      status: "ACTIVE",
      departments: [{ departmentId: 3, name: "Marketing" }],
    },
  ]);
}

function company(
  id: number,
  name: string,
  tradeName: string,
  documentNumber: string,
  department: string,
  district: string,
) {
  return {
    companyId: id,
    name,
    tradeName,
    documentNumber,
    address: "Av. Javier Prado Este N.° 2450",
    logoUrl: null,
    phone: "987654321",
    email: `contacto${id}@inclub.com`,
    status: "ACTIVE",
    interior: "Of. 302",
    postalCode: "15036",
    districtCode: "150130",
    urbanArea: district,
    phoneCountryCode: "+51",
    secondaryPhoneCountryCode: "+51",
    secondaryPhone: "4567890",
    registryNumber: `REG-INS-${documentNumber}`,
    generalDataConfigured: true,
    departmentName: department,
    districtName: district,
  };
}

function employee(
  id: number,
  companyId: number,
  code: string,
  firstName: string,
  lastName: string,
  secondLastName: string,
  documentNumber: string,
  gender: string,
  positionId: number,
  _position: string,
) {
  return {
    employeeId: id,
    identityUserId: 1000 + id,
    companyId,
    branchId: 1,
    departmentId: (id % 5) + 1,
    positionId,
    countryId: 173,
    cityId: 1,
    geoDepartmentCode: "15",
    geoProvinceCode: "1501",
    districtCode: "150130",
    leaderEmployeeId: id === 1 ? null : 1,
    employeeCode: code,
    documentTypeId: 1,
    documentTypeCode: "DNI",
    documentNumber,
    firstName,
    lastName,
    secondLastName,
    birthDate: `199${id}-05-${String(id + 6).padStart(2, "0")}`,
    gender,
    personalEmail: `${firstName.split(" ")[0].toLowerCase()}@gmail.com`,
    corporateEmail: `${code.toLowerCase()}@inclub.com`,
    phoneCountryCode: "+51",
    phoneNumber: `9876543${String(id).padStart(2, "0")}`,
    address: "Lima, Perú",
    timezone: "America/Lima",
    hiredAt: `202${id % 5}-02-19`,
    photoUrl: null,
    status: id === 4 ? "TERMINATED" : "ACTIVE",
    createdAt: NOW,
    updatedAt: NOW,
  };
}

function catalogItem(id: number, code: string, name: string) {
  return { id, code, name, numericValue: null, active: true };
}
function benefit(
  id: number,
  code: string,
  name: string,
  type: string,
  imageUrl: string,
) {
  return {
    benefitId: id,
    companyId: 1,
    code,
    name,
    description: `${name}. Beneficio disponible para colaboradores.`,
    type,
    validFrom: "2026-01-01",
    validUntil: "2026-12-31",
    imageUrl,
    termsUrl: null,
    active: true,
    createdByUserId: 1,
    updatedByUserId: 1,
    createdAt: NOW,
    updatedAt: NOW,
  };
}
function benefitRequests() {
  return EMPLOYEES.map((item, index) => ({
    benefitRequestId: index + 1,
    benefitId: (index % BENEFITS.length) + 1,
    companyId: item.companyId,
    employeeId: item.employeeId,
    redeemDate: "2026-09-19",
    message: "Solicito acceder al beneficio.",
    attachmentUrl: null,
    status: index < 3 ? "PENDING" : index === 3 ? "REJECTED" : "APPROVED",
    decisionMessage: null,
    requestedByUserId: item.identityUserId,
    decidedByUserId: null,
    decidedAt: null,
    cancelledByUserId: null,
    cancelledAt: null,
    createdAt: NOW,
    updatedAt: NOW,
  }));
}
function agreements() {
  return CONTRACTS.map((contract, index) => ({
    agreementId: index + 1,
    agreementNumber: `AGR-2026-${String(index + 1).padStart(4, "0")}`,
    parentAgreementId: null,
    templateId: 1,
    type: "EMPLOYMENT",
    title: "Contrato de trabajo",
    sourceType: "HR_CONTRACT",
    sourceReferenceId: contract.contractId,
    startDate: contract.startDate,
    endDate: contract.endDate,
    variables: {},
    parties: [],
    status: index === 1 ? "ACTIVE" : "PENDING_SIGNATURE",
    currentVersion: 1,
    currentDocumentUrl: "/assets/demo/contrato-modelo.pdf",
    generatedAt: NOW,
    submittedAt: NOW,
    effectiveAt: index === 1 ? NOW : null,
    expiredAt: null,
    terminatedAt: null,
    terminationReason: null,
    createdByUserId: 1,
    createdAt: NOW,
    updatedAt: NOW,
  }));
}
function agreementTemplates() {
  return [
    {
      templateId: 1,
      code: "EMPLOYMENT_STANDARD",
      version: 1,
      type: "EMPLOYMENT",
      name: "Contrato laboral estándar",
      description: "Plantilla demostrativa para Staff",
      content: "Contrato laboral de {{employeeName}} con {{companyName}}.",
      status: "ACTIVE",
      createdByUserId: 1,
      createdAt: NOW,
      updatedAt: NOW,
    },
  ];
}
function documentTypes() {
  return [
    {
      code: "DNI",
      authorityCode: "01",
      name: "Documento Nacional de Identidad",
      category: "IDENTITY",
      maxLength: 8,
      exactLength: true,
      numericOnly: true,
      active: true,
    },
    {
      code: "CE",
      authorityCode: "04",
      name: "Carné de Extranjería",
      category: "IDENTITY",
      maxLength: 12,
      exactLength: false,
      numericOnly: false,
      active: true,
    },
    {
      code: "RUC",
      authorityCode: "06",
      name: "Registro Único de Contribuyentes",
      category: "BUSINESS",
      maxLength: 11,
      exactLength: true,
      numericOnly: true,
      active: true,
    },
  ];
}
function countries() {
  return [
    { id: 1, countryDescription: "Peru", iso: "PE", niceName: "Peru", iso3: "PER", numericCode: 604, phoneCode: 51, symbol: "S/", courtesy: "", icon: "" },
    { id: 2, countryDescription: "Estados Unidos", iso: "US", niceName: "Estados Unidos", iso3: "USA", numericCode: 840, phoneCode: 1, symbol: "$", courtesy: "", icon: "" },
    { id: 3, countryDescription: "Zona Euro", iso: "EU", niceName: "Zona Euro", iso3: "EUR", numericCode: 978, phoneCode: 0, symbol: "€", courtesy: "", icon: "" }
  ];
}

function currencies() {
  return [
    { idCurrency: 1, code: "PEN", name: "SOLES", symbol: "S/", decimalPlaces: 2, active: true, idCountry: 1 },
    { idCurrency: 2, code: "USD", name: "DÓLARES", symbol: "$", decimalPlaces: 2, active: true, idCountry: 2 },
    { idCurrency: 3, code: "EUR", name: "EURO", symbol: "€", decimalPlaces: 2, active: true, idCountry: 3 }
  ];
}
function taxUnits() {
  return [
    {
      taxUnitId: 1,
      startDate: "2026-01-01",
      endDate: "2026-12-31",
      acronym: "UIT",
      description: "Unidad Impositiva Tributaria 2026",
      amount: 5350,
      active: true,
      createdAt: NOW,
      updatedAt: NOW,
    },
    {
      taxUnitId: 2,
      startDate: "2025-01-01",
      endDate: "2025-12-31",
      acronym: "UIT",
      description: "Unidad Impositiva Tributaria 2025",
      amount: 5150,
      active: true,
      createdAt: NOW,
      updatedAt: NOW,
    },
  ];
}
function pensionFunds() {
  return [
    {
      pensionFundId: 1,
      abbreviation: "PM",
      name: "Prima",
      insuranceCap: 7906.8,
      mandatoryContributionPercent: 10,
      disabilityInsurancePercent: 1.36,
      commissionOnRemunerationPercent: 1.95,
      mixedCommissionPercent: 0,
      constructionContributionPercent: 0,
      miningContributionPercent: 0,
      afpNetCode: "PRIMA",
      accountingAnnex: "AFP-01",
      rtpsCode: "21",
      active: true,
      createdAt: NOW,
      updatedAt: NOW,
    },
    {
      pensionFundId: 2,
      abbreviation: "IN",
      name: "Integra",
      insuranceCap: 7906.8,
      mandatoryContributionPercent: 10,
      disabilityInsurancePercent: 1.42,
      commissionOnRemunerationPercent: 1.8,
      mixedCommissionPercent: 0,
      constructionContributionPercent: 0,
      miningContributionPercent: 0,
      afpNetCode: "INTEGRA",
      accountingAnnex: "AFP-02",
      rtpsCode: "22",
      active: true,
      createdAt: NOW,
      updatedAt: NOW,
    },
  ];
}
function geoDepartments() {
  return [
    { code: "15", name: "Lima" },
    { code: "13", name: "La Libertad" },
    { code: "14", name: "Lambayeque" },
    { code: "20", name: "Piura" },
  ];
}
function geoProvinces() {
  return [
    { code: "1501", departmentCode: "15", name: "Lima" },
    { code: "1502", departmentCode: "15", name: "Barranca" },
  ];
}
function geoDistricts() {
  return [
    {
      ubigeoCode: "150130",
      provinceCode: "1501",
      name: "San Borja",
      capital: "San Borja",
      naturalRegion: "Costa",
    },
    {
      ubigeoCode: "150131",
      provinceCode: "1501",
      name: "San Isidro",
      capital: "San Isidro",
      naturalRegion: "Costa",
    },
    {
      ubigeoCode: "150122",
      provinceCode: "1501",
      name: "Miraflores",
      capital: "Miraflores",
      naturalRegion: "Costa",
    },
  ];
}
function taxRates() {
  return [
    {
      taxRateId: 1,
      code: "IGV",
      name: "Impuesto General a las Ventas",
      rate: 18,
      effectiveFrom: "2026-01-01",
      effectiveTo: null,
      active: true,
      source: "SUNAT",
      createdAt: NOW,
      updatedAt: NOW,
    },
  ];
}
function catalogs() {
  return [
    {
      catalogId: 1,
      code: "PRODUCT_CATEGORY",
      name: "Categorías de producto",
      description: "Clasificación comercial",
      metadataSchema: {},
      active: true,
      createdAt: NOW,
      updatedAt: NOW,
    },
  ];
}
function catalogItems() {
  return [
    {
      itemId: 1,
      catalogId: 1,
      parentItemId: null,
      code: "SERVICES",
      name: "Servicios",
      description: "Servicios corporativos",
      metadata: {},
      sortOrder: 1,
      active: true,
      createdAt: NOW,
      updatedAt: NOW,
    },
  ];
}
function customers() {
  return [
    {
      customerId: 1,
      companyId: 1,
      code: "CLI-001",
      customerType: "LEGAL",
      documentTypeCode: "RUC",
      documentNumber: "20123456789",
      legalName: "Cliente Demo S.A.C.",
      tradeName: "Cliente Demo",
      email: "contacto@cliente-demo.pe",
      phone: "987654321",
      address: "Lima",
      districtCode: "150130",
      segmentItemId: null,
      classification: "A",
      active: true,
      createdAt: NOW,
      updatedAt: NOW,
    },
  ];
}
function products() {
  return [
    {
      productId: 1,
      companyId: 1,
      code: "SRV-001",
      productType: "SERVICE",
      name: "Servicio corporativo",
      description: "Servicio demo",
      categoryItemId: 1,
      unitCode: "ZZ",
      currencyCode: "PEN",
      unitPrice: 2500,
      taxCode: "IGV",
      active: true,
      createdAt: NOW,
      updatedAt: NOW,
    },
  ];
}
function sctrProviders() {
  return [
    {
      sctrId: 1,
      name: "RIMAC",
      ruc: "20505643390",
      ratePercent: 2,
      insuranceCap: 0,
      type: "HEALTH",
      createdAt: NOW,
      updatedAt: NOW,
    },
    {
      sctrId: 2,
      name: "PACÍFICO",
      ruc: "20332970411",
      ratePercent: 3,
      insuranceCap: 0,
      type: "HEALTH",
      createdAt: NOW,
      updatedAt: NOW,
    },
  ];
}
function bankEntities() {
  return [
    {
      bankEntityId: 1,
      abbreviation: "BCP",
      name: "Banco de Crédito del Perú",
      createdAt: NOW,
      updatedAt: NOW,
    },
    {
      bankEntityId: 2,
      abbreviation: "BBVA",
      name: "Banco Continental",
      createdAt: NOW,
      updatedAt: NOW,
    },
    {
      bankEntityId: 3,
      abbreviation: "IBK",
      name: "Interbank",
      createdAt: NOW,
      updatedAt: NOW,
    },
  ];
}
function workerTypes() {
  return [
    {
      workerTypeId: 1,
      name: "Ejecutivo",
      rtpsCode: "20",
      active: true,
      createdAt: NOW,
      updatedAt: NOW,
    },
    {
      workerTypeId: 2,
      name: "Empleado",
      rtpsCode: "21",
      active: true,
      createdAt: NOW,
      updatedAt: NOW,
    },
  ];
}

function organizationDepartments() {
  return [
    "Recursos Humanos",
    "Tecnología",
    "Finanzas",
    "Marketing",
    "Logística",
  ].map((name, index) => ({ departmentId: index + 1, name }));
}
function branches() {
  return [
    {
      branchId: 1,
      companyId: 1,
      name: "Sede Lima",
      address: "San Isidro, Lima",
      phone: "987654321",
      status: "ACTIVE",
    },
  ];
}
function positions() {
  return [
    "Backend",
    "Analista",
    "Jefe TI",
    "Diseñadora UX/UI",
    "Frontend",
    "Gerente General",
  ].map((name, index) => ({
    positionId: index + 1,
    companyId: 1,
    name,
    description: name,
    requirements: null,
    salaryMin: 1800,
    salaryMax: 8000,
    status: "ACTIVE",
  }));
}
function costCenters() {
  return [
    {
      costCenterId: 1,
      companyId: 1,
      code: "CC-RRHH",
      name: "Recursos Humanos",
    },
  ];
}
function legalRepresentatives() {
  return EMPLOYEES.slice(0, 3).map((item, index) => ({
    legalRepresentativeId: index + 1,
    companyId: item.companyId,
    firstName: item.firstName,
    lastName: `${item.lastName} ${item.secondLastName}`,
    documentType: "DNI",
    documentNumber: item.documentNumber,
    phoneCountryCode: "+51",
    phone: item.phoneNumber,
    nationality: "Perú",
    departmentId: 1,
    email: item.corporateEmail,
    status: "ACTIVE",
  }));
}
function payrollResponsibles() {
  return EMPLOYEES.slice(0, 3).map((item, index) => ({
    payrollResponsibleId: index + 1,
    companyId: item.companyId,
    firstName: item.firstName,
    lastName: `${item.lastName} ${item.secondLastName}`,
    documentType: "DNI",
    documentNumber: item.documentNumber,
    phoneCountryCode: "+51",
    phone: item.phoneNumber,
    nationality: "Perú",
    departmentId: 1,
    email: item.corporateEmail,
    status: "ACTIVE",
  }));
}
function bankAccounts() {
  return COMPANIES.map((item, index) => ({
    bankAccountId: index + 1,
    companyId: item.companyId,
    paymentTypeCode: "PAYROLL_DEPOSIT",
    accountTypeCode: index % 2 ? "SAVINGS" : "CHECKING",
    bankCode: index % 2 ? "BBVA" : "INTERBANK",
    currencyCode: index % 2 ? "PEN" : "USD",
    accountNumber: `00124578963${index}`,
    status: "ACTIVE",
    defaultAccount: index === 0,
  }));
}
function companyParameters() {
  return COMPANIES.map((item, index) => ({
    companyParameterId: index + 1,
    companyId: item.companyId,
    dailyWorkHours: 8,
    timeRegistrationFormat: "HH_MM",
    laborRegime: "PRIVATE_GENERAL",
    digitalSignatureEnabled: index !== 2,
    signatureFileName: "firma-demo.png",
    signatureContentType: "image/png",
    signatureData: null,
  }));
}
function lawModifications() {
  return COMPANIES.map((item, index) => ({
    companyLawModificationId: index + 1,
    companyId: item.companyId,
    modifications: [
      {
        position: 1,
        description: "Ley 30334: Ley de inafectación de las gratificaciones",
        effectiveDate: "2015-02-14",
      },
      {
        position: 2,
        description: "Nueva estructura AFP-NET vigente",
        effectiveDate: "2026-01-01",
      },
    ],
  }));
}
function digitalPayslips() {
  return COMPANIES.map((item, index) => ({
    companyDigitalPayslipId: index + 1,
    companyId: item.companyId,
    certificateFileName: "Certificado_modelo.pdf",
    certificateContentType: "application/pdf",
    certificateData: null,
    digitalSignatureEnabled: true,
  }));
}
